import io from 'socket.io-client';
import { RootStore } from '@store';
import { action, computed, makeObservable, observable } from 'mobx';
import { checkBetAmountWithinLimits, isCrypto } from '@helpers';
import { PurseType } from 'types/enums';
import i18next from 'i18next';
import { UtilsLocalStorage } from 'services';
import { GAME_CONFIG_BOOMERANG } from '@store/GameConfigBoomerang';
import { GAME_CONFIG_4RABET } from '@store/GameConfig4rabet';
import { GAME_CONFIG_NEW_YEAR } from './GameConfigNewYear';
import { GAME_CONFIG_DIVER_FAST } from './GameConfigDiverFast';
import { DEFAULT_GAME_CONFIG } from '@store/GameConfigDiver';
import { getApiUrl } from '../helpers/get-api-url';

export class UserStore {
    public io: typeof io.Socket | null;

    public gameConfig: { skinId?: string };

    constructor(private rootStore: RootStore) {
        makeObservable(this);
        this.io = null;
        this.gameConfig = {};
    }

    @observable id: string | null = null;
    @observable username: string | null = null;
    @observable avatar: string | null = null;
    @observable operatorId: string = '';
    @observable balance: number = 0;
    @observable token: string = '';
    @observable currency: PurseType = PurseType.USD;
    @observable currencies = {};
    @observable betsConfig: null | {
        minBetAmount: string;
        maxBetAmount: string;
        maxWinAmount?: string;
        defaultBetAmount?: string;
        betPresets: string[];
    } = null;
    @observable isConnectWS = false;
    @observable isPromoCodeEnabled: boolean = false;

    @observable minBetAmount: string = '0.10';
    @observable defaultBetAmount: string = '0.10';
    @observable maxBetAmount: string = '50000.00';
    @observable maxWinAmount: string | undefined = undefined;

    @computed
    public get amountDigits(): number {
        return isCrypto(this.currency) ? 8 : 2;
    }

    @action
    public setUserId(id: string): void {
        this.id = id;
    }

    @action
    public setUserName(name: string): void {
        this.username = name;
    }

    @action
    public setAvatar(avatar: string): void {
        this.avatar = avatar;
    }

    @action
    public setBalance(amount: number): void {
        if (typeof amount === 'string') amount = parseFloat(amount);
        this.balance = amount || 0;
    }

    @action
    public setCurrency(currency: PurseType): void {
        this.currency = currency;
    }

    @action
    public setIsPromoCodeEnabled(isPromoCodeEnabled: boolean): void {
        this.isPromoCodeEnabled = isPromoCodeEnabled;
    }

    @computed
    public get betRanges(): [number, number] {
        return [+this.minBetAmount, +this.maxBetAmount];
    }

    @computed
    public get defaultBet(): number {
        return +this.defaultBetAmount;
    }

    @computed
    public get defaultBetValue(): number {
        const betsConfig = this.rootStore.userStore.betsConfig;
        if (!betsConfig) return 1;
        let maxBet = parseFloat(betsConfig.maxBetAmount);
        if (maxBet > this.balance) maxBet = this.balance;

        const bet = checkBetAmountWithinLimits(
            parseFloat(betsConfig.defaultBetAmount ?? betsConfig.minBetAmount),
            parseFloat(betsConfig.minBetAmount),
            maxBet,
        );
        return bet === 0 ? parseFloat(betsConfig.minBetAmount) : bet;
    }

    @action
    private _setBetsConfig(betsConfig: {
        [key: string]: {
            minBetAmount: string;
            maxBetAmount: string;
            maxWinAmount?: string;
            defaultBetAmount: string;
            betPresets: string[];
        };
    }): void {
        this.minBetAmount = betsConfig[this.currency].minBetAmount.replace(/(\.0+|0+)$/, '');
        this.maxBetAmount = betsConfig[this.currency].maxBetAmount.replace(/(\.0+|0+)$/, '');
        this.defaultBetAmount = betsConfig[this.currency].defaultBetAmount?.replace(/(\.0+|0+)$/, '');

        if (betsConfig[this.currency] && betsConfig[this.currency].maxWinAmount)
            this.maxWinAmount = betsConfig[this.currency].maxWinAmount!.replace(/(\.0+|0+)$/, '');
        else this.maxWinAmount = undefined;

        this.betsConfig = betsConfig[this.currency];
    }

    @action
    private _setCurrencies(currencies) {
        UtilsLocalStorage.setItem('currencies', JSON.stringify(currencies));
        this.currencies = currencies;
    }

    @action
    connectWS() {
        this.isConnectWS = true;
    }

    get gameMode() {
        return window.location.pathname.split('/')[3];
    }

    private _getParams() {
        const urlParams = new URLSearchParams(window.location.search);
        const operatorId = urlParams.get('operator');
        const authToken = urlParams.get('auth_token');
        const currency = urlParams.get('currency');
        const lang = urlParams.get('lang');
        const skinId = urlParams.get('skinId');

        return { operatorId, authToken, currency, lang, skinId };
    }

    private changeLanguage(lang) {
        const langs = i18next.languages;
        i18next.changeLanguage(langs.includes(lang) ? lang : 'en');
    }

    @action
    private async fetchAuthToken(): Promise<string | undefined> {
        const requestUrl = getApiUrl(window.env.REACT_APP_HTTP_URL)
        const { operatorId, authToken, currency, skinId } = this._getParams();

        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                operator: operatorId,
                auth_token: authToken,
                currency,
                game_mode: this.gameMode,
            }),
        };

        let token = '';

        const response = await fetch(`${requestUrl}/api/auth`, requestOptions);
        const { success, result, isLobbyEnabled, isMusicEnabled, isSoundEnabled, isPromoCodeEnabled } =
            await response.json();

        if (skinId === 'diver-boomerang') this.gameConfig = GAME_CONFIG_BOOMERANG;
        else if (skinId === 'diver-4rabet') this.gameConfig = GAME_CONFIG_4RABET;
        else if (skinId === 'new-year') this.gameConfig = GAME_CONFIG_NEW_YEAR;
        else if (this.gameMode === 'diver-fast') this.gameConfig = GAME_CONFIG_DIVER_FAST;
        else if (!skinId) this.gameConfig = DEFAULT_GAME_CONFIG;

        if (success) {
            token = result;
            window.env.JWT = token;
            window.env.IS_LOBBY_ENABLED = isLobbyEnabled;
            window.env.IS_MUSIC_ENABLED = isMusicEnabled || false;
            window.env.IS_SOUND_ENABLED = isSoundEnabled || false;
            this.setIsPromoCodeEnabled(isPromoCodeEnabled ?? false);
        }

        return token;
    }

    @action
    public async instance() {
        const { authToken, currency, operatorId, lang } = this._getParams();
        this.operatorId = operatorId as string;
        this.setCurrency((currency ?? PurseType.DEMO) as PurseType);
        this.changeLanguage(lang);

        if (!authToken || !currency) {
            this.rootStore.websocketService.init(operatorId, '', this.gameMode);
        } else {
            const token = await this.fetchAuthToken();
            this.token = token || '';
            this.rootStore.websocketService.init(operatorId, token, this.gameMode);
        }

        this.handleWebSocketEvents();
    }

    handleWebSocketEvents() {
        this.io = this.rootStore.websocketService.ws;

        if (!this.io) {
            return;
        }

        this.io.on('onConnectGame', ({ player }) => {
            const { userId, balance, currency } = player;
            this.setUserId(userId);
            this.setBalance(balance);
            this.setCurrency(currency);
        });

        this.io.on('myData', (data) => {
            const { gameAvatar, nickname, userId } = data;
            this.setUserId(userId);
            this.setUserName(nickname);
            this.setAvatar(gameAvatar);
        });

        this.io.on('onCurrencyChange', ({ currency, balance }) => {
            this.setBalance(balance);
            this.setCurrency(currency);
        });

        this.io.on('onBalanceChange', ({ currency, balance }) => {
            if (this.currency === currency) this.setBalance(balance);
        });

        this.io.on('currencies', this._setCurrencies.bind(this));

        this.io.on('betsConfig', this._setBetsConfig.bind(this));

        this.io?.on('connect', () => this.connectWS());
    }
}